import type {
  CmmdProjectTaskDraft,
  CmmdProjectTaskDraftPreview,
  CmmdProjectTaskDraftProposal,
} from "@cmmd-forge/contracts";

import { CmmdApiError, cmmdApiClient } from "~/environments/cmmdApi";
import { createInitialProposal } from "./taskDraftModel";
import {
  errorMessage,
  isCreateIdempotencyConflict,
  isDefinitiveCreateFailure,
  pendingCreateStorageKey,
  activeDraftStorageKey,
  randomKey,
  storedDraftPointer,
  type ProjectLeadTaskDraftStartInput,
  type StoredDraftPointer,
} from "./useProjectLeadTaskDraft.logic";

/**
 * The create-attempt / commit-attempt bookkeeping for a Project Lead task
 * draft, split out of useProjectLeadTaskDraft.logic.ts (which owns everything
 * NOT about pending-attempt identity) purely to stay under the per-file line
 * cap — both files are the same seam, divided along "restore & storage
 * pointers" vs. "in-flight attempt reservations."
 */
export interface DraftCreateAttempt {
  readonly idempotencyKey: string;
  readonly proposal: CmmdProjectTaskDraftProposal;
  readonly environmentId?: string;
  readonly forgeThreadId?: string;
}

const createAttemptReservations = new Map<string, Promise<void>>();

export async function withCreateAttemptReservation<T>(
  ownerId: string,
  projectId: number,
  operation: () => Promise<T> | T,
): Promise<T> {
  const key = pendingCreateStorageKey(ownerId, projectId);
  if (navigator.locks !== undefined) {
    return navigator.locks.request(key, operation);
  }
  const previous = createAttemptReservations.get(key) ?? Promise.resolve();
  let release!: () => void;
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.then(() => current);
  createAttemptReservations.set(key, tail);
  await previous;
  try {
    return await operation();
  } finally {
    release();
    if (createAttemptReservations.get(key) === tail) createAttemptReservations.delete(key);
  }
}

export function readCreateAttempt(
  ownerId: string | null,
  projectId: number,
): DraftCreateAttempt | null {
  if (ownerId === null) return null;
  try {
    const value = window.localStorage.getItem(pendingCreateStorageKey(ownerId, projectId));
    if (value === null) return null;
    const parsed = JSON.parse(value) as Partial<DraftCreateAttempt>;
    return typeof parsed.idempotencyKey === "string" && parsed.proposal?.schemaVersion === 1
      ? (parsed as DraftCreateAttempt)
      : null;
  } catch {
    return null;
  }
}

export function writeCreateAttempt(
  ownerId: string,
  projectId: number,
  attempt: DraftCreateAttempt,
): boolean {
  try {
    window.localStorage.setItem(
      pendingCreateStorageKey(ownerId, projectId),
      JSON.stringify(attempt),
    );
    return true;
  } catch {
    // CMMD idempotency remains authoritative when browser storage is unavailable.
    return false;
  }
}

export function removeCreateAttempt(
  ownerId: string,
  projectId: number,
  idempotencyKey: string,
): void {
  try {
    const key = pendingCreateStorageKey(ownerId, projectId);
    const current = readCreateAttempt(ownerId, projectId);
    if (current?.idempotencyKey === idempotencyKey) window.localStorage.removeItem(key);
  } catch {
    // Storage may be unavailable.
  }
}

export function retireSupersededCreateAttempt(input: {
  readonly attempt: DraftCreateAttempt | null;
  readonly ownerId: string | null;
  readonly projectId: number;
  readonly clearAttempt: () => void;
}): void {
  input.clearAttempt();
  if (input.attempt !== null && input.ownerId !== null) {
    removeCreateAttempt(input.ownerId, input.projectId, input.attempt.idempotencyKey);
  }
}

export function retireDefinitiveCreateAttempt(input: {
  readonly cause: unknown;
  readonly ownerId: string | null;
  readonly projectId: number;
  readonly attempt: DraftCreateAttempt | null;
}): boolean {
  if (!isDefinitiveCreateFailure(input.cause)) return false;
  if (input.ownerId !== null && input.attempt !== null) {
    removeCreateAttempt(input.ownerId, input.projectId, input.attempt.idempotencyKey);
  }
  return true;
}

export function discardError(cause: unknown): {
  readonly stale: boolean;
  readonly message: string;
} {
  const stale = cause instanceof CmmdApiError && cause.status === 409;
  return {
    stale,
    message: stale
      ? "This draft changed before it could be discarded. Reload the latest version, then try again."
      : errorMessage(cause, "Forge could not discard this task draft. Try again."),
  };
}

export interface DraftCommitAttempt {
  readonly idempotencyKey: string;
  readonly expectedVersion: number;
}

const SUPERSEDED_DRAFT_REQUEST = Symbol("superseded-draft-request");

export function assertCurrentRequest(
  generationRef: { readonly current: number },
  generation: number,
): void {
  if (generationRef.current !== generation) throw SUPERSEDED_DRAFT_REQUEST;
}

export function settleCurrentRequest(
  generationRef: { readonly current: number },
  generation: number,
  settle: () => void,
): void {
  if (generationRef.current === generation) settle();
}

export function requestWasSuperseded(
  generationRef: { readonly current: number },
  generation: number,
  cause: unknown,
): boolean {
  return generationRef.current !== generation || cause === SUPERSEDED_DRAFT_REQUEST;
}

export function applyCreatedDraft(input: {
  readonly created: CmmdProjectTaskDraft;
  readonly ownerId: string;
  readonly projectId: number;
  readonly setDraft: (draft: CmmdProjectTaskDraft) => void;
  readonly setProposal: (proposal: CmmdProjectTaskDraftProposal | null) => void;
  readonly setSelectedRef: (localRef: string | null) => void;
  readonly createIdempotencyKey: string;
  readonly environmentId: string;
}): boolean {
  let pointerStored = false;
  try {
    window.localStorage.setItem(
      activeDraftStorageKey(input.ownerId, input.projectId),
      JSON.stringify({ draftId: input.created.id, environmentId: input.environmentId }),
    );
    pointerStored = true;
  } catch {
    // The durable CMMD draft remains authoritative when browser storage is unavailable.
  }
  if (pointerStored) {
    removeCreateAttempt(input.ownerId, input.projectId, input.createIdempotencyKey);
  }
  input.setDraft(input.created);
  input.setProposal(input.created.proposal);
  input.setSelectedRef(input.created.proposal?.tasks[0]?.localRef ?? null);
  return pointerStored;
}

export async function resolveCommitAttempt(
  pending: DraftCommitAttempt | null,
  validate: () => Promise<CmmdProjectTaskDraftPreview | null>,
): Promise<DraftCommitAttempt | null> {
  if (pending !== null) return pending;
  const preview = await validate();
  return preview ? { idempotencyKey: randomKey("commit"), expectedVersion: preview.version } : null;
}

export function draftCreateAttempt(
  current: DraftCreateAttempt | null,
  projectId: number,
  startInput: ProjectLeadTaskDraftStartInput,
): DraftCreateAttempt {
  if (current !== null) return current;
  return {
    idempotencyKey: randomKey("draft"),
    proposal: createInitialProposal({
      projectId,
      title: startInput.title.trim() || "New task tree",
      description: startInput.description,
      localRef: randomKey("task"),
      priority: startInput.priority,
      workItemType: startInput.workItemType,
    }),
  };
}

export async function createDraftWithIdempotencyRecovery(input: {
  readonly projectId: number;
  readonly attempt: DraftCreateAttempt;
  readonly threadId: string;
  readonly onRotate: (attempt: DraftCreateAttempt) => void;
}): Promise<CmmdProjectTaskDraft> {
  const create = (attempt: DraftCreateAttempt) =>
    cmmdApiClient.createProjectTaskDraft(String(input.projectId), {
      createIdempotencyKey: attempt.idempotencyKey,
      forgeThreadId: input.threadId,
      proposal: attempt.proposal,
    });
  try {
    return await create(input.attempt);
  } catch (cause: unknown) {
    if (!isCreateIdempotencyConflict(cause)) throw cause;
    const rotatedAttempt = { ...input.attempt, idempotencyKey: randomKey("draft") };
    input.onRotate(rotatedAttempt);
    return create(rotatedAttempt);
  }
}

export function startOwnerId(
  restoring: boolean,
  busy: boolean,
  ownerId: string | null,
): string | null {
  return restoring || busy ? null : ownerId;
}

export function retireCommitAttemptForVersion(
  attemptRef: { current: DraftCommitAttempt | null },
  version: number,
): void {
  if (attemptRef.current?.expectedVersion !== version) attemptRef.current = null;
}

export type ActiveDraftReservation = {
  readonly existing: CmmdProjectTaskDraft;
  readonly pointer: StoredDraftPointer;
};

export async function readActiveDraftReservation(
  ownerId: string,
  projectId: number,
): Promise<ActiveDraftReservation | null> {
  const pointer = storedDraftPointer(ownerId, projectId);
  if (pointer === null) return null;
  const existing = await cmmdApiClient.getProjectTaskDraft(String(projectId), pointer.draftId);
  return { existing, pointer };
}

function terminalDraftKind(draft: CmmdProjectTaskDraft): "committed" | "discarded" | "active" {
  if (draft.status === "committed") return "committed";
  return draft.status === "discarded" ? "discarded" : "active";
}

export function isActiveDraftReservation(
  reservation: ActiveDraftReservation | { readonly attempt: DraftCreateAttempt },
): reservation is ActiveDraftReservation {
  return "existing" in reservation;
}

export function applyReservedExistingDraft(input: {
  readonly reservation: ActiveDraftReservation;
  readonly applyCommitted: (draft: CmmdProjectTaskDraft) => void;
  readonly applyDiscarded: (draftId: number) => void;
  readonly applyActive: (draft: CmmdProjectTaskDraft, pointer: StoredDraftPointer) => void;
}): void {
  const kind = terminalDraftKind(input.reservation.existing);
  if (kind === "committed") input.applyCommitted(input.reservation.existing);
  else if (kind === "discarded") input.applyDiscarded(input.reservation.existing.id);
  else input.applyActive(input.reservation.existing, input.reservation.pointer);
}
